import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Music, Target, Heart, Users, Globe, Award, TrendingUp, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MarketingHeader } from "@/components/MarketingHeader";
import { MarketingFooter } from "@/components/MarketingFooter";
import { MarketingBackground } from "@/components/MarketingBackground";
import { BackgroundShapes } from "@/components/BackgroundShapes";
import { SEO } from "@/components/SEO";

const About = () => {
  const navigate = useNavigate();

  const stats = [
    {
      value: "500+",
      label: "Happy Students",
      icon: Users
    },
    {
      value: "50+",
      label: "Countries",
      icon: Globe
    },
    {
      value: "10,000+",
      label: "Lessons Taught",
      icon: Award
    },
    {
      value: "4.9/5",
      label: "Average Rating",
      icon: Sparkles
    }
  ];

  const values = [
    {
      icon: Music,
      title: "Quality Education",
      description: "Every coach is vetted to ensure professional expertise and teaching excellence. We maintain the highest standards for vocal instruction."
    },
    {
      icon: Target,
      title: "Accessibility",
      description: "Breaking down barriers to vocal education with transparent pricing and worldwide availability. Learn from anywhere, anytime."
    },
    {
      icon: Heart,
      title: "Student Success",
      description: "Your growth is our mission. We provide tools and insights to accelerate your vocal development and track your progress."
    },
    {
      icon: TrendingUp,
      title: "Innovation",
      description: "Combining expert instruction with cutting-edge technology to create the most effective learning experience possible."
    }
  ];

  const timeline = [
    {
      year: "2023",
      title: "The Beginning",
      description: "Founded with a mission to make quality vocal education accessible to everyone, everywhere."
    },
    {
      year: "2024",
      title: "Platform Launch",
      description: "Launched our integrated platform with video conferencing, progress tracking, and seamless booking."
    },
    {
      year: "2025",
      title: "Global Expansion",
      description: "Reached students in over 50 countries, building a worldwide community of passionate vocalists."
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <MarketingBackground />
      <div className="pt-[73px]">
        <SEO
        title="About Us - Professional Vocal Coaching Platform"
        description="Learn about Vocalzing's mission to revolutionize vocal education with expert coaches, real-time video lessons, and data-driven progress tracking for singers worldwide."
        keywords="vocal coaching platform, online singing lessons, vocal education, music education technology, professional voice training"
        canonical="https://vocalzing.lovable.app/about"
      />
      <MarketingHeader />

      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden" aria-label="About hero section">
        <BackgroundShapes variant="about" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        <div className="container mx-auto text-center relative z-10 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            About{" "}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Vocalzing
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 animate-slide-up max-w-3xl mx-auto leading-relaxed">
            Empowering aspiring vocalists worldwide with expert coaching, innovative technology, and data-driven insights
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-muted/30 relative">
        <BackgroundShapes variant="features" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-muted-foreground font-medium">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 relative">
        <BackgroundShapes variant="testimonials" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <Card className="border-2 border-primary/20 shadow-2xl bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
            <CardContent className="pt-12 pb-12 px-8 md:px-16 relative z-10">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Mission</h2>
              </div>
              <p className="text-xl md:text-2xl text-center leading-relaxed text-foreground mb-8">
                To empower aspiring vocalists worldwide by connecting them with expert coaches, 
                providing data-driven insights into their progress, and creating a seamless platform 
                for transformative musical education.
              </p>
              <div className="flex justify-center">
                <Button 
                  size="lg"
                  onClick={() => navigate("/auth")}
                >
                  Start Your Journey
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 bg-muted/30 relative">
        <BackgroundShapes variant="pricing" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Story</h2>
            <p className="text-lg text-muted-foreground">The journey of building something special</p>
          </div>

          <div className="space-y-8">
            {timeline.map((item, index) => (
              <Card key={index} className="border-border/40 hover-lift bg-card/50 backdrop-blur">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg">
                      {item.year}
                    </div>
                    <CardTitle className="text-2xl">{item.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed text-foreground pl-20">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 relative">
        <BackgroundShapes variant="hero" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do and help us create the best experience for our students
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-border/40 hover-lift bg-card/50 backdrop-blur">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2">{value.title}</CardTitle>
                        <CardDescription className="text-base leading-relaxed text-foreground">
                          {value.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Students Choose Us</h2>
            <p className="text-lg text-muted-foreground">Everything you need in one comprehensive platform</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-border/40 hover-lift bg-card/50 backdrop-blur text-center">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <Music className="w-8 h-8 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl mb-3">Expert Coaches</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Learn from experienced vocal professionals who are passionate about your success
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-border/40 hover-lift bg-card/50 backdrop-blur text-center">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl mb-3">Progress Analytics</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Track your improvement with detailed insights and visual analytics
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-border/40 hover-lift bg-card/50 backdrop-blur text-center">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                </div>
                <CardTitle className="text-xl mb-3">Learn Anywhere</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Flexible scheduling and integrated video conferencing for lessons anywhere
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Join Our Community</h2>
            <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Become part of a global community of passionate vocalists transforming their voices with expert guidance
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6" 
                onClick={() => navigate("/auth")}
              >
                Get Started Today
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6"
                onClick={() => navigate("/pricing")}
              >
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
      </div>
    </div>
  );
};

export default About;
